import { zodResolver } from '@hookform/resolvers/zod';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import {
  COUPON_APPLICABLE_ON,
  COUPON_APPLICABLE_TYPES,
  COUPON_CSV_COLUMNS,
  COUPON_TYPES,
  COUPON_USES,
  couponGenerateSchema,
  couponInputSchema,
} from '@app/shared';
import type { CouponDto, CouponType, Paged } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { FormDialog } from '../../components/dialogs';
import {
  MoneyInput,
  NumberInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { ApiError, api } from '../../lib/api';
import { ReleasedFeature } from '../../release';
import { formatCents, formatDateTime } from '../../lib/format';
import { withRequiredMessages } from '../inventory/formUtils';

/** Calendar date as the reference shows it: MM-DD-YYYY. */
const usDate = (iso: string): string => `${iso.slice(5, 7)}-${iso.slice(8, 10)}-${iso.slice(0, 4)}`;
const couponValue = (c: Pick<CouponDto, 'type' | 'value'>): string =>
  c.type === 'Fixed' ? formatCents(c.value).replace('$', '$ ') : `${c.value} %`;

interface CouponForm {
  code: string;
  name: string;
  type: CouponType | '';
  value: number | null;
  startDate: string;
  endDate: string;
  taxIncluded: 'Yes' | 'No';
  usesPerCoupon: (typeof COUPON_USES)[number];
  usesCount: number | null;
  applicableOn: (typeof COUPON_APPLICABLE_ON)[number] | '';
  applicableType: (typeof COUPON_APPLICABLE_TYPES)[number];
  recurringMonths: number | null;
  waiveProcessingFee: 'Yes' | 'No';
  usesPerCustomer: number | null;
  status: 'Active' | 'Inactive';
  /** "Bulk Coupon Generation" only. */
  count: number | null;
}
const EMPTY: CouponForm = {
  code: '',
  name: '',
  type: '',
  value: null,
  startDate: '',
  endDate: '',
  taxIncluded: 'No',
  usesPerCoupon: 'One-Time Use',
  usesCount: null,
  applicableOn: '',
  applicableType: 'One-Time',
  recurringMonths: null,
  waiveProcessingFee: 'No',
  usesPerCustomer: null,
  status: 'Active',
  count: null,
};

type Mode = { kind: 'add' } | { kind: 'edit'; coupon: CouponDto };

const REQUIRED = {
  type: 'Select Coupon Type',
  value: 'Coupon Value is required',
  applicableOn: 'Select Coupon Applicable On',
  usesPerCustomer: 'Uses Per Customer is required',
  count: 'Number of Coupons is required',
} as const;
const YES_NO = toOptions(['Yes', 'No']);
const GENERATION = ['Single Coupon Generation', 'Bulk Coupon Generation'] as const;

/** "Add Coupon Code" (one coupon, or many with the same terms) and "Update Coupon". */
function CouponDialog({ mode, onClose }: { mode: Mode | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [bulk, setBulk] = useState(false);
  const { control, handleSubmit, reset, setError, watch } = useForm<CouponForm>({
    defaultValues: EMPTY,
    // In bulk mode the code typed is the prefix of the generated codes and may be empty.
    resolver: ((values, context, options) =>
      withRequiredMessages<CouponForm>(
        (bulk
          ? zodResolver(couponGenerateSchema)
          : zodResolver(couponInputSchema)) as unknown as Resolver<CouponForm>,
        REQUIRED,
        // The generate schema calls the code its prefix.
      )(
        bulk ? ({ ...values, prefix: values.code } as CouponForm) : values,
        context,
        options,
      )) as Resolver<CouponForm>,
  });
  const type = watch('type');
  const usesPerCoupon = watch('usesPerCoupon');
  const applicableType = watch('applicableType');

  useEffect(() => {
    if (!mode) return;
    setBulk(false);
    reset(
      mode.kind === 'edit'
        ? {
            ...EMPTY,
            code: mode.coupon.code,
            name: mode.coupon.name,
            type: mode.coupon.type,
            value: mode.coupon.value,
            startDate: mode.coupon.startDate,
            endDate: mode.coupon.endDate,
            taxIncluded: mode.coupon.taxIncluded,
            usesPerCoupon: mode.coupon.usesPerCoupon,
            usesCount: mode.coupon.usesCount,
            applicableOn: mode.coupon.applicableOn,
            applicableType: mode.coupon.applicableType,
            recurringMonths: mode.coupon.recurringMonths,
            waiveProcessingFee: mode.coupon.waiveProcessingFee,
            usesPerCustomer: mode.coupon.usesPerCustomer,
            status: mode.coupon.status,
          }
        : EMPTY,
    );
  }, [mode, reset]);

  const save = useMutation({
    mutationFn: (values: CouponForm): Promise<CouponDto | { created: number }> => {
      const { count, code, ...terms } = values;
      // In bulk mode `terms` holds the validated prefix and no code.
      if (bulk) return api.post<{ created: number }>('/coupons/generate', { ...terms, count });
      return mode?.kind === 'edit'
        ? api.put<CouponDto>(`/coupons/${mode.coupon.id}`, { ...terms, code })
        : api.post<CouponDto>('/coupons', { ...terms, code });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coupons'] }),
  });

  const submit = handleSubmit(async (values) => {
    try {
      const result = await save.mutateAsync(values);
      notify.success(
        'created' in result
          ? `${result.created} coupon(s) generated.`
          : mode?.kind === 'edit'
            ? 'Coupon updated.'
            : 'Coupon added.',
      );
      onClose();
    } catch (err) {
      // The API calls the code of a bulk generation its prefix.
      const moved =
        bulk && err instanceof ApiError
          ? Object.assign(err, {
              details: err.details.map((d) => (d.path === 'prefix' ? { ...d, path: 'code' } : d)),
            })
          : err;
      if (!applyServerErrors(moved, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={Boolean(mode)}
      title={mode?.kind === 'edit' ? 'Update Coupon' : 'Add Coupon Code'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      maxWidth="md"
    >
      {mode?.kind === 'add' ? (
        <RadioGroup
          row
          aria-label="Coupon generation"
          value={bulk ? GENERATION[1] : GENERATION[0]}
          onChange={(e) => setBulk(e.target.value === GENERATION[1])}
          sx={{ mb: 1.5 }}
        >
          {GENERATION.map((g) => (
            <FormControlLabel key={g} value={g} label={g} control={<Radio size="small" />} />
          ))}
        </RadioGroup>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        {bulk ? (
          <NumberInput
            control={control}
            name="count"
            label="Number of Coupons"
            required
            emptyValue={null}
            helperText="e.g., 10"
          />
        ) : null}
        <TextInput
          control={control}
          name="code"
          label="Coupon Code"
          required={!bulk}
          autoFocus
          helperText={
            bulk ? 'Optional: the start of each generated code (10 characters at most).' : undefined
          }
          slotProps={{ htmlInput: { maxLength: bulk ? 10 : 30 } }}
        />
        <TextInput
          control={control}
          name="name"
          label="Coupon Name"
          required
          slotProps={{ htmlInput: { maxLength: 50 } }}
        />
        <SelectInput
          control={control}
          name="type"
          label="Coupon Type"
          required
          emptyLabel="Select"
          options={toOptions(COUPON_TYPES)}
        />
        {type === 'Percent' ? (
          <NumberInput
            control={control}
            name="value"
            label="Coupon Value (%)"
            required
            emptyValue={null}
          />
        ) : (
          <MoneyInput control={control} name="value" label="Coupon Value($)" required />
        )}
        <TextInput
          control={control}
          name="startDate"
          label="Coupon Start Date"
          type="date"
          required
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextInput
          control={control}
          name="endDate"
          label="Coupon End Date"
          type="date"
          required
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <SelectInput
          control={control}
          name="taxIncluded"
          label="Is tax included?"
          required
          options={YES_NO}
        />
        <SelectInput
          control={control}
          name="usesPerCoupon"
          label="Uses Per Coupon"
          required
          options={toOptions(COUPON_USES)}
        />
        {usesPerCoupon === 'Multiple Use' ? (
          <NumberInput
            control={control}
            name="usesCount"
            label="Number of Uses"
            required
            emptyValue={null}
          />
        ) : null}
        <SelectInput
          control={control}
          name="applicableOn"
          label="Coupon Applicable On"
          required
          emptyLabel="Select"
          options={toOptions(COUPON_APPLICABLE_ON)}
        />
        <SelectInput
          control={control}
          name="applicableType"
          label="Coupon Applicable Type"
          required
          options={toOptions(COUPON_APPLICABLE_TYPES)}
        />
        {applicableType === 'Recurring' ? (
          <NumberInput
            control={control}
            name="recurringMonths"
            label="Recurring Month Count"
            required
            emptyValue={null}
          />
        ) : null}
        <SelectInput
          control={control}
          name="waiveProcessingFee"
          label="Do you want waive off processing fee?"
          required
          options={YES_NO}
        />
        <NumberInput
          control={control}
          name="usesPerCustomer"
          label="Uses Per Customer"
          required
          emptyValue={null}
        />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(['Active', 'Inactive'])}
        />
      </Box>
    </FormDialog>
  );
}

const HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
} as const;

/** "CSV Bulk Upload Coupon": a file with the template's columns; all rows or none. */
function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const upload = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('file', f);
      return api.upload<{ created: number }>('/coupons/upload', form);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coupons'] }),
  });

  useEffect(() => {
    if (open) {
      setFile(null);
      setProblems([]);
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setProblems(['Choose a CSV file']);
      return;
    }
    try {
      const result = await upload.mutateAsync(file);
      notify.success(`${result.created} coupon(s) uploaded.`);
      onClose();
    } catch (err) {
      setProblems(
        err instanceof ApiError && err.details.length
          ? err.details.map((d) => `${d.path}: ${d.message}`)
          : [err instanceof Error ? err.message : 'The file could not be processed.'],
      );
    }
  };

  return (
    <FormDialog
      open={open}
      title="CSV Bulk Upload Coupon"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={upload.isPending}
      submitLabel="Upload"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button component="label" role={undefined} tabIndex={-1} variant="outlined">
          Choose CSV file
          <Box
            component="input"
            type="file"
            accept=".csv,text/csv"
            aria-label="CSV file"
            sx={HIDDEN}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFile(e.target.files?.[0] ?? null);
              setProblems([]);
            }}
          />
        </Button>
        <Typography>{file ? file.name : 'No file chosen'}</Typography>
        <Button component="a" href={api.url('/coupons/template')} download>
          Download Sample File
        </Button>
      </Box>
      <FormHelperText>
        Header: {COUPON_CSV_COLUMNS.join(', ')}. Coupon Type is Fixed or Percent; the value is in
        dollars or percent; dates are MM-DD-YYYY. A file with a bad row adds nothing.
      </FormHelperText>
      {problems.map((p) => (
        <FormHelperText key={p} error>
          {p}
        </FormHelperText>
      ))}
    </FormDialog>
  );
}

/** Coupon codes (PC852), the reference's "Coupon Code" page. */
export function CouponsPage() {
  const [draft, setDraft] = useState('');
  const [code, setCode] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mode, setMode] = useState<Mode | null>(null);
  const [uploading, setUploading] = useState(false);
  const coupons = useQuery({
    queryKey: ['coupons', 'list', code, page, pageSize],
    queryFn: () => api.get<Paged<CouponDto>>('/coupons', { code, page, pageSize }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<CouponDto>[] = [
    { key: '#', header: '#', width: 50, render: (_c, i) => rowNumber(i, page, pageSize) },
    { key: 'code', header: 'Coupon Code', render: (c) => c.code },
    { key: 'name', header: 'Coupon Name', render: (c) => c.name },
    { key: 'type', header: 'Coupon Type', render: (c) => c.type },
    { key: 'value', header: 'Coupon Value', nowrap: true, render: (c) => couponValue(c) },
    {
      key: 'startDate',
      header: 'Coupon Start Date',
      nowrap: true,
      render: (c) => usDate(c.startDate),
    },
    { key: 'endDate', header: 'Coupon End Date', nowrap: true, render: (c) => usDate(c.endDate) },
    { key: 'createdBy', header: 'Created By', render: (c) => c.createdBy?.loginId ?? '' },
    {
      key: 'createdAt',
      header: 'Created Date',
      nowrap: true,
      render: (c) => formatDateTime(c.createdAt),
    },
    // The reference shows "---" for a coupon nobody has changed.
    { key: 'updatedBy', header: 'Updated By', render: (c) => c.updatedBy?.loginId ?? '---' },
    {
      key: 'updatedAt',
      header: 'Updated Date',
      nowrap: true,
      render: (c) => (c.updatedAt ? formatDateTime(c.updatedAt) : '---'),
    },
    {
      key: 'action',
      header: 'Action',
      render: (c) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            aria-label={`Edit coupon ${c.code}`}
            onClick={() => setMode({ kind: 'edit', coupon: c })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Coupon Code"
        code="PC852"
        actions={
          <>
            <Button variant="contained" onClick={() => setUploading(true)}>
              CSV Bulk Upload Coupon
            </Button>
            <Button
              variant="contained"
              component="a"
              href={api.url('/coupons/export', { source: 'upload' })}
              download
            >
              Download Uploaded Coupon
            </Button>
            <Button
              variant="contained"
              component="a"
              href={api.url('/coupons/export', { source: 'generated' })}
              download
            >
              View Bulk Generated Coupon
            </Button>
            <Button variant="contained" onClick={() => setMode({ kind: 'add' })}>
              Add Coupon Code
            </Button>
            <ReleasedFeature name="coupon-usage-report">
              <Button variant="contained" component={Link} to="/coupons/report">
                View Report
              </Button>
            </ReleasedFeature>
          </>
        }
      />
      <Paper
        variant="outlined"
        component="form"
        aria-label="Coupon search"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setCode(draft.trim());
          setPage(1);
        }}
        sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <TextField
          label="Enter Coupon Code"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          sx={{ maxWidth: 360 }}
        />
        <Button type="submit" variant="contained" color="info">
          Submit
        </Button>
      </Paper>
      <DataTable
        label="Coupon Code"
        columns={columns}
        rows={coupons.data?.data}
        rowKey={(c) => c.id}
        loading={coupons.isFetching}
        error={coupons.error}
        onRetry={() => void coupons.refetch()}
        emptyText="No record found"
        page={page}
        pageSize={pageSize}
        total={coupons.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      <CouponDialog mode={mode} onClose={() => setMode(null)} />
      <UploadDialog open={uploading} onClose={() => setUploading(false)} />
    </Box>
  );
}
